/**
 * Configure wizard — Step 1: delivery email.
 * Prefilled from the authenticated user's email; the leads also land in the
 * caller's Run 1 queue (banner copy is verbatim from the design/plan).
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';

interface StepEmailProps {
  deliveryEmail: string;
  onEmailChange: (email: string) => void;
  onNext: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function StepEmail({ deliveryEmail, onEmailChange, onNext }: StepEmailProps) {
  const { user } = useAuth();

  // Prefill ONCE from the auth email. The ref stops the effect from re-firing
  // when the user clears the field — without it, backspacing the last character
  // instantly re-populated the full email (canary bug, 2026-07-18).
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!prefilledRef.current && !deliveryEmail && user?.email) {
      prefilledRef.current = true;
      onEmailChange(user.email);
    }
  }, [user, deliveryEmail, onEmailChange]);

  const valid = EMAIL_RE.test(deliveryEmail.trim());

  return (
    <div className="buyleads-card">
      <h2 style={{ margin: '0 0 6px', color: 'var(--gold)' }}>Delivery Email</h2>
      <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '14px' }}>
        We'll email your leads here. Suppression is matched against this address.
      </p>

      <div className="buyleads-note" style={{ marginBottom: '18px' }}>
        Your leads will also be loaded into your AI caller call queue for Run 1 — no upload needed.
      </div>

      <div className="form-group" style={{ maxWidth: '420px' }}>
        <label>Email Address</label>
        <input
          type="email"
          value={deliveryEmail}
          onChange={(e) => onEmailChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid) onNext(); }}
          placeholder="you@example.com"
          autoFocus
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button onClick={onNext} disabled={!valid} className="btn btn-primary">
          Continue
        </button>
      </div>
    </div>
  );
}
